import React from "react";

/** Matches a set of characters (alphanumeric, underscored, or hyphen) surrounded by curly braces, capturing the characters inside the curly braces */
const innerCurlyBraceRegex = /\{([-\w]+)\}/;

/** Same as above regex but gets the entire token (curly braces included) */
const entireCurlyBraceRegex = /({[-\w]+\})/;

/** Wrap React.isValidElement so it correctly matches to JSX.Element */
const isJsxElement = (element: any): element is JSX.Element => React.isValidElement(element);

export interface TokensWithNodes {
    readonly [token: string]: JSX.Element | string;
}

/** Split textWithTokens on each token */
export const splitTextWithTokens = (textWithTokens: string): readonly string[] => {
    return textWithTokens.split(entireCurlyBraceRegex).filter(el => el !== "");
};

/** Replace tokens in textSplitWithTokens with the values from tokensAndValues */
export const replaceTokensWithValues = (textSplitWithTokens: readonly string[], tokensAndValues: TokensWithNodes, updateReactNodeValue?: (token: JSX.Element) => JSX.Element): readonly React.ReactNode[] => {
    return textSplitWithTokens.reduce((result, substring, index) => {
        let stringValue = substring;
        const tokenName = innerCurlyBraceRegex.exec(substring)?.[1];

        // If the substring is a token that exists in tokensAndValues, use that value, else use the original substring
        if (tokenName && tokensAndValues.hasOwnProperty(tokenName)) {
            let reactNodeValue = tokensAndValues[tokenName];
            if (isJsxElement(reactNodeValue)) {
                //Callback to possbily update the token.
                reactNodeValue = updateReactNodeValue ? updateReactNodeValue(reactNodeValue) : reactNodeValue;
                // Each element in a React.ReactNode list needs a unique key
                result.push(React.cloneElement(reactNodeValue, { key: index }));
                return result;
            } else {
                stringValue = reactNodeValue ?? "";
            }
        }

        // For string values, join with a previous string in the list if possible
        const resultLen = result.length;
        if (resultLen && typeof result[resultLen - 1] === "string") {
            result[resultLen - 1] += stringValue;
        } else {
            result.push(stringValue);
        }

        return result;
    }, [] as React.ReactNode[]);
};
